import { Link } from 'react-router-dom';
import { LandingShell } from './LandingShell';
import {
  AgencySubmissionIcon,
  CastingRequestIcon,
  PersonalArchiveIcon,
  BrandPitchIcon,
} from './UseCaseIcons';

const REQUEST_ACCESS_MAILTO =
  'mailto:henrik@hallengrens.com?subject=Vuelette%20%E2%80%93%20Request%20access%20(model)';

export default function LandingModels() {
  return (
    <LandingShell currentPage="models">
      <header className="vl-ap-hero">
        <div
          className="vl-hero-image-bg"
          style={{ backgroundImage: 'url(/landing-img/hero-model-v6.jpg)' }}
          aria-hidden
        />
        <div className="vl-ap-hero-inner">
          <div className="vl-ap-hero-text">
            <Link to="/" className="vl-back-link">← For everyone</Link>
            <div className="vl-section-eyebrow">For models · 01</div>
            <h1>
              Your portfolio is yours <em>— wherever the work was made.</em>
            </h1>
            <p className="vl-lead">
              Models work with dozens of photographers a year. Every shoot
              lives somewhere else: a WeTransfer link that expires, a Google
              Drive folder you've forgotten the password for, an iCloud
              shared album from 2022. Vuelette gathers it all in one quiet
              place that stays yours — even after the people change.
            </p>
          </div>
        </div>
      </header>

      <section className="vl-section">
        <div className="vl-container">
          <div className="vl-split vl-split-right">
            <div className="vl-split-text">
              <div className="vl-section-eyebrow">The problem we solve</div>
              <h2 className="vl-section-title">
                You spend more time hunting images <em>than presenting them.</em>
              </h2>
              <p className="vl-section-lead">
                Agency submissions, casting requests, brand pitches —
                every one needs a fresh selection of your best work. And
                every time, you're scrolling through old DMs to find that
                one shoot from last summer.
              </p>
              <ul className="vl-features">
                <li>
                  <span className="vl-feat-icon">⌘</span>
                  <div>
                    <strong>Every shoot in one place</strong>
                    <span>Photographers can share their session with you directly. Or you can import images yourself.</span>
                  </div>
                </li>
                <li>
                  <span className="vl-feat-icon">⌷</span>
                  <div>
                    <strong>Build submission-ready portfolios in minutes</strong>
                    <span>Pull from any shoot. Add measurements and contact info. Send a single link instead of a 1 GB zip.</span>
                  </div>
                </li>
                <li>
                  <span className="vl-feat-icon">⊞</span>
                  <div>
                    <strong>Living links</strong>
                    <span>Update your set — every portfolio you've ever sent reflects the new version. No more "v3_FINAL_actually.zip".</span>
                  </div>
                </li>
              </ul>
            </div>
            <div className="vl-split-image">
              <img src="/landing-img/model-card-v4.jpg" alt="" />
            </div>
          </div>
        </div>
      </section>

      <section className="vl-section vl-section-tinted">
        <div className="vl-container">
          <div className="vl-section-eyebrow">Use cases</div>
          <h2 className="vl-section-title">
            Three ways models use Vuelette <em>every week.</em>
          </h2>

          <div className="vl-usecase-grid">
            <div className="vl-usecase">
              <div className="vl-usecase-icon"><AgencySubmissionIcon /></div>
              <span className="vl-badge">Agency submission</span>
              <h4>A single link, signed and trackable.</h4>
              <p>
                Send your editorial selects to an agency with a passcode.
                See who opened it, when, and from where — without the
                awkward follow-up email.
              </p>
            </div>
            <div className="vl-usecase">
              <div className="vl-usecase-icon"><CastingRequestIcon /></div>
              <span className="vl-badge">Casting request</span>
              <h4>Different portfolios for different rooms.</h4>
              <p>
                Beauty casting needs close-ups. Commercial casting needs
                range. Build a portfolio per audience, all from the same
                curated library.
              </p>
            </div>
            <div className="vl-usecase">
              <div className="vl-usecase-icon"><PersonalArchiveIcon /></div>
              <span className="vl-badge">Personal archive</span>
              <h4>Your work, kept safe — forever.</h4>
              <p>
                When a photographer leaves Vuelette or deletes a session,
                your copy of the shoot stays. You always have a permanent
                record of work made with you.
              </p>
            </div>
            <div className="vl-usecase">
              <div className="vl-usecase-icon"><BrandPitchIcon /></div>
              <span className="vl-badge">Brand pitch</span>
              <h4>Show range, not just the best ten.</h4>
              <p>
                Pair a "primary" set of editorial work with a "secondary"
                set showing your commercial range. Both shown together,
                arranged the way you'd lay out a printed comp card.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="vl-cta-block">
        <div className="vl-container">
          <h2 className="vl-section-title" style={{ marginInline: 'auto' }}>
            Make submissions feel <em>like a portfolio,</em> not a homework upload.
          </h2>
          <a href={REQUEST_ACCESS_MAILTO} className="vl-btn vl-btn-primary">
            Request access →
          </a>
          <p style={{ marginTop: '1.5rem' }}>
            <Link to="/" style={{ color: 'var(--vl-text-3)' }}>← Back to hub</Link>
          </p>
        </div>
      </section>
    </LandingShell>
  );
}
